import React, { useState, useRef, useCallback, useEffect } from 'react';
import HeroBanner from '../components/tw1/HeroBanner';
import { CalendarCheck, Upload, FileText, Trash2, AlertTriangle, Info, Calculator, TrendingUp, BarChart3, ChevronDown, ChevronUp, ChevronRight, ChevronLeft, Check, Download } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Skeleton } from '../components/ui/skeleton';
import { toast } from 'sonner';
import api from '../lib/api';
import * as XLSX from 'xlsx';
import { PTW_LOGO, TRAVIS_LOGO, EXACT_LOGO } from '../lib/logos';

const MONTH_NAMES = ['januari','februari','maart','april','mei','juni','juli','augustus','september','oktober','november','december'];

function formatEUR(val) {
  if (val === undefined || val === null) return '€ 0,00';
  return `€ ${val.toLocaleString('nl-NL', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}
function formatBedrag(val) {
  if (val === undefined || val === null || val === 0) return '';
  return val.toLocaleString('nl-NL', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}
function lastDayOfMonth(year, month) {
  const d = new Date(year, month, 0);
  return `${String(d.getDate()).padStart(2,'0')}-${String(d.getMonth()+1).padStart(2,'0')}-${year}`;
}
const round2 = (n) => Math.round((n + Number.EPSILON) * 100) / 100;

/* ═══════════════════════════════════════════════════════════
   PARSING MODULE — Alle CSV parsing in frontend
   User's EXACTE code — NIET WIJZIGEN
   ═══════════════════════════════════════════════════════════ */

function parseCSVLine(line, separator) {
  const result = [];
  let current = "";
  let inQuotes = false;
  for (let i = 0; i < line.length; i++) {
    const ch = line[i];
    if (inQuotes) {
      if (ch === '"') {
        if (i+1 < line.length && line[i+1] === '"') {
          current += '"'; i++;
        } else {
          inQuotes = false;
        }
      } else {
        current += ch;
      }
    } else {
      if (ch === '"') {
        inQuotes = true;
      } else if (ch === separator) {
        result.push(current.trim());
        current = "";
      } else {
        current += ch;
      }
    }
  }
  result.push(current.trim());
  return result;
}

const readFileAsRows = async (file) => {
  const text = await file.text();
  const lines = text.split("\n").filter(l => l.trim());
  const sep = lines[0]?.includes(";") ? ";" : ",";
  return lines.map(l => {
    const clean = l.replace(/\r/g,"").replace(/^\ufeff/,"");
    return parseCSVLine(clean, sep);
  });
};

const parseNL = (s) => {
  if (s === null || s === undefined || s === "") return 0;
  let str = String(s).replace(/[€\s]/g,"").trim();
  const hasComma = str.includes(",");
  const hasDot = str.includes(".");
  if (hasComma && hasDot) {
    const lastComma = str.lastIndexOf(",");
    const lastDot = str.lastIndexOf(".");
    if (lastComma > lastDot) {
      str = str.replace(/\./g,"").replace(",",".");
    } else {
      str = str.replace(/,/g,"");
    }
  } else if (hasComma && !hasDot) {
    str = str.replace(",",".");
  }
  return parseFloat(str) || 0;
};

const findColIdx = (headers, ...names) => {
  const lower = headers.map(h => String(h).trim().toLowerCase().replace(/[\s-]+/g,"_").replace(/^\ufeff/,""));
  for (const n of names) {
    const idx = lower.indexOf(n.toLowerCase().replace(/[\s-]+/g,"_"));
    if (idx !== -1) return idx;
  }
  return -1;
};

const SHORT_MAP = {
  "aalsmeer":"TW1 Aalsmeer BV", "amsterdam":"TW1 Amsterdam BV",
  "asten":"TW1 Asten BV", "bodegraven":"TW1 Bodegraven BV",
  "ede":"TW1 Ede BV", "eindhoven":"TW1 Eindhoven BV",
  "holten":"TW1 Holten BV", "maasvlakte":"TW1 Maasvlakte BV",
  "rilland":"TW1 Rilland BV", "roosendaal":"TW1 Roosendaal BV",
  "rotterdam":"TW1 Rotterdam BV", "utrecht":"TW1 Utrecht BV",
  "venlo":"TW1 Venlo BV", "groenlo":"TW1 Groenlo BV",
  "steenwijk":"TW1 Steenwijk BV", "wehl":"TW1 Wehl BV",
  "duiven":"TW1 Wehl BV", "doetinchem":"TW1 Groenlo BV|DOET",
  "hazeldonk":"TW1 Hazeldonk BV", "truckstop 8":"Truckstop 8 BV"
};

const SKIP_LOCATIONS = ["administratie"];

function mapLocatie(raw) {
  let s = String(raw||"").trim().toLowerCase();
  s = s.replace(/truckwash\s+1\s*/,"").replace(/tw1?\s*/,"");
  s = s.replace(/\s*b\.?v\.?\s*$/,"").replace(/[()]/g,"");
  s = s.replace(/\s+/g," ").trim();
  if (SKIP_LOCATIONS.some(skip => s.includes(skip))) return null;
  return SHORT_MAP[s] || null;
}

const ORDER_EXCLUDE_PREFIXES = [
  "klant dkv","klant multitankcard","klant cargocard","klant cargo card",
  "klant contant","klant pin","klant travelcard","cargo card"
];

const parseConsolidatedOrders = (rowsRaw) => {
  if (!rowsRaw || rowsRaw.length < 2) return { data:{}, errors:["Leeg"] };

  const headers = rowsRaw[0].map(h => String(h).trim());
  const orderIdx  = findColIdx(headers, "order_nummer","ordernummer","order_id","opdracht_id");
  const locIdx    = findColIdx(headers, "locatie_gewassen","opdracht_locatie","locatie");
  const klantIdx  = findColIdx(headers, "klant_naam","klantnaam","klant","klant_bedrijfsnaam");
  const bedragIdx = findColIdx(headers, "totale_opdrachtwaarde","totaal_exclusief","bedrag_exc_btw","bedrag","totaal");

  if (locIdx === -1 || bedragIdx === -1) return { data:{}, errors:["Kolommen niet gevonden"] };

  // STAP 1: Dedupliceer op order_nummer
  const orderMap = new Map();
  for (let i = 1; i < rowsRaw.length; i++) {
    const row = rowsRaw[i];
    if (!row || row.every(c => !c && c !== 0)) continue;
    const orderNr = orderIdx !== -1 ? String(row[orderIdx]||"").trim() : "__row_"+i;
    if (!orderNr || orderMap.has(orderNr)) continue; // SKIP duplicaten
    orderMap.set(orderNr, {
      bedrag: parseNL(row[bedragIdx]),
      locRaw: String(row[locIdx]||"").trim(),
      klantRaw: klantIdx !== -1 ? String(row[klantIdx]||"").replace(/^"+|"+$/g, '').trim() : ""
    });
  }

  // STAP 2: Per unieke order → locatie + exclusie
  const data = {};
  let totalAll=0, totalDoorb=0, excluded=0;

  for (const [, order] of orderMap) {
    const rawMap = mapLocatie(order.locRaw);
    if (!rawMap) continue; // skip Administratie, unmapped

    // Split "TW1 Groenlo BV|DOET" into entity + kostenplaats
    const [tw1, kp] = rawMap.includes('|') ? rawMap.split('|') : [rawMap, ''];

    totalAll += order.bedrag;
    if (!data[tw1]) data[tw1] = { aantal:0, bedrag:0, bedragDoorb:0, doet_bedrag:0, doet_bedragDoorb:0 };
    data[tw1].aantal++;
    data[tw1].bedrag += order.bedrag; // ALLES incl DKV etc (fee-basis)

    const klantLower = order.klantRaw.replace(/^"+|"+$/g, '').toLowerCase().replace(/\s+/g, ' ').trim();
    const isExcl = ORDER_EXCLUDE_PREFIXES.some(p => klantLower.startsWith(p));
    if (isExcl) { excluded++; }
    else {
      totalDoorb += order.bedrag;
      data[tw1].bedragDoorb += order.bedrag; // alleen non-excluded (doorbelasting)
    }

    // Track Doetinchem sub-stream separately
    if (kp === 'DOET') {
      data[tw1].doet_bedrag += order.bedrag;
      if (!isExcl) data[tw1].doet_bedragDoorb += order.bedrag;
    }
  }

  return { data, stats:{ totalAll, totalDoorb, excluded, uniqueOrders: orderMap.size } };
};

/* ═══ LOCATION DATA (voor fee/export berekeningen) ═══ */

const LOCATION_DATA = {
  "TW1 Aalsmeer BV":    { rc_group_loc: "13130096", rc_loc_group: "13130091", gb_holding: "1482AALS", fee_pct: 0.11 },
  "TW1 Amsterdam BV":   { rc_group_loc: "13130093", rc_loc_group: "13130219", gb_holding: "1482AMST", fee_pct: 0.11 },
  "TW1 Asten BV":       { rc_group_loc: "13130093", rc_loc_group: "13130105", gb_holding: "1482ASTE", fee_pct: 0.11 },
  "TW1 Bodegraven BV":  { rc_group_loc: "13130096", rc_loc_group: "13130099", gb_holding: "1482BODE", fee_pct: 0.11 },
  "TW1 Ede BV":         { rc_group_loc: "13130094", rc_loc_group: "13130093", gb_holding: "1482EDE",  fee_pct: 0.11 },
  "TW1 Eindhoven BV":   { rc_group_loc: "13130094", rc_loc_group: "150",      gb_holding: "1482EIND", fee_pct: 0.11 },
  "TW1 Groenlo BV":     { rc_group_loc: "3",        rc_loc_group: "13130615", gb_holding: "1482GROE", fee_pct: 0.11 },
  "TW1 Holten BV":      { rc_group_loc: "3",        rc_loc_group: "13130277", gb_holding: "1482HOLT", fee_pct: 0.11 },
  "TW1 Maasvlakte BV":  { rc_group_loc: "13130094", rc_loc_group: "87",       gb_holding: "1482MAAS", fee_pct: 0.11 },
  "TW1 Rilland BV":     { rc_group_loc: "13130105", rc_loc_group: "113677",   gb_holding: "1482RILL", fee_pct: 0.11 },
  "TW1 Roosendaal BV":  { rc_group_loc: "13130105", rc_loc_group: "130283",   gb_holding: "1482ROOS", fee_pct: 0.11 },
  "TW1 Rotterdam BV":   { rc_group_loc: "130278",   rc_loc_group: "130235",   gb_holding: "1482ROTT", fee_pct: 0.11 },
  "TW1 Steenwijk BV":   { rc_group_loc: "3",        rc_loc_group: "13130613", gb_holding: "1482STEE", fee_pct: 0.11 },
  "TW1 Utrecht BV":     { rc_group_loc: "130245",   rc_loc_group: "130269",   gb_holding: "1482UTRE", fee_pct: 0.11 },
  "TW1 Venlo BV":       { rc_group_loc: "13130093", rc_loc_group: "13130221", gb_holding: "1482VENL", fee_pct: 0.11 },
  "TW1 Wehl BV":        { rc_group_loc: "3",        rc_loc_group: "13130614", gb_holding: "1482WEHL", fee_pct: 0.11 },
  "TW1 Hazeldonk BV":   { rc_group_loc: "13130647",  rc_loc_group: "3",          gb_holding: "1482HAZE", fee_pct: 0.11 },
  "Truckstop 8 BV":     { rc_group_loc: "13130094", rc_loc_group: "13130266", gb_holding: "1482ACHT", fee_pct: 0.05 },
};

/* ═══ PTW4 PARSER (frontend) ═══ */

const PTW4_MAPPING = [
  [["velder"], "TW1 Bodegraven BV"],
  [["stef", "bodegraven"], "TW1 Bodegraven BV"],
  [["dachser"], "TW1 Holten BV"],
  [["stef", "zeewolde"], "TW1 Holten BV"],
  [["dhl", "roosendaal"], "TW1 Roosendaal BV"],
  [["dhl"], "TW1 Eindhoven BV"],
  [["dlg"], "TW1 Eindhoven BV"],
  [["quatra"], "TW1 Amsterdam BV"],
  [["rosier"], "TW1 Utrecht BV"],
  [["avarent"], "TW1 Utrecht BV"],
  [["dehaas"], "TW1 Aalsmeer BV"],
  [["de haas"], "TW1 Aalsmeer BV"],
  [["doetinchem"], "TW1 Groenlo BV|DOET"],
  [["hazeldonk"], "TW1 Hazeldonk BV"],
];

function mapPTW4Location(customerName) {
  const lower = String(customerName||"").trim().toLowerCase();
  for (const [patterns, location] of PTW4_MAPPING) {
    if (patterns.length === 1) {
      if (lower.includes(patterns[0])) return location;
    } else {
      if (patterns.every(p => lower.includes(p))) return location;
    }
  }
  return null;
}

function preprocessPTW4Text(rawText) {
  /**
   * Preprocessing voor Exact Online CSV-exports die elke regel in outer quotes wrappen.
   * Input:  "9260,119419,""De Haas Road Cargo B.V."",Nederland,...,2237.55,..."
   * Output: 9260,119419,"De Haas Road Cargo B.V.",Nederland,...,2237.55,...
   */
  return rawText.split("\n").map(line => {
    let l = line.replace(/\r/g, "");
    if (l.length >= 2 && l[0] === '"' && l[l.length - 1] === '"') {
      l = l.slice(1, -1);
      l = l.replace(/""/g, '"');
    }
    return l;
  }).join("\n");
}

function parsePTW4Orders(rowsRaw) {
  const debug = { headers: [], klantIdx: -1, klantNaam: '?', bedragIdx: -1, bedragNaam: '?', rows: [], perLocatie: {} };
  if (!rowsRaw || rowsRaw.length < 2) return { data: {}, stats: { total: 0, unmapped: 0 }, warnings: [], debug };
  const headers = rowsRaw[0].map(h => String(h).trim());
  debug.headers = headers;
  let klantIdx = -1;
  for (let i = 0; i < headers.length; i++) {
    const h = headers[i].toLowerCase();
    if (h.includes("naam") && !h.includes("nummer")) { klantIdx = i; break; }
  }
  if (klantIdx === -1) {
    for (let i = 0; i < headers.length; i++) {
      const h = headers[i].toLowerCase();
      if (h.includes("klant") && !h.includes("nummer") && !h.includes("nr")) { klantIdx = i; break; }
    }
  }
  if (klantIdx === -1) {
    for (let i = 0; i < headers.length; i++) {
      const h = headers[i].toLowerCase();
      if (h.includes("relatie") && !h.includes("nummer") && !h.includes("nr") && !h.includes("code")) { klantIdx = i; break; }
    }
  }
  const bedragIdx = findColIdx(headers, "bedrag", "totaal", "omzet", "bedrag_exc_btw");
  debug.klantIdx = klantIdx;
  debug.klantNaam = klantIdx >= 0 && klantIdx < headers.length ? headers[klantIdx] : '?';
  debug.bedragIdx = bedragIdx;
  debug.bedragNaam = bedragIdx >= 0 && bedragIdx < headers.length ? headers[bedragIdx] : '?';
  if (klantIdx === -1 || bedragIdx === -1) return { data: {}, stats: { total: 0, unmapped: 0 }, warnings: ["PTW4: kolommen niet gevonden"], debug };

  const data = {};
  let total = 0, unmapped = 0;
  const warnings = [];
  for (let i = 1; i < rowsRaw.length; i++) {
    const row = rowsRaw[i];
    if (!row || row.every(c => !c && c !== 0)) continue;
    total++;
    const customer = String(row[klantIdx]||"").trim();
    const bedragRaw = row[bedragIdx] !== undefined ? String(row[bedragIdx]) : '';
    const amount = parseNL(bedragRaw);
    const location = mapPTW4Location(customer);
    // Debug: bewaar eerste 10 rijen met ALLE kolommen
    if (debug.rows.length < 10) {
      debug.rows.push({
        rij: i,
        alleCellen: row.map((c, ci) => ({ idx: ci, kol: headers[ci] || `[${ci}]`, waarde: String(c) })),
        klantRaw: customer,
        bedragRaw: bedragRaw,
        bedragParsed: amount,
        locatie: location || '(niet gemapped)',
      });
    }
    if (!location) {
      unmapped++;
      if (unmapped <= 5) warnings.push(`PTW4: klant '${customer}' niet gemapped`);
    } else {
      // Handle "TW1 Groenlo BV|DOET" pipe-separated kostenplaats
      const [entity, kp] = location.includes('|') ? location.split('|') : [location, ''];
      data[entity] = (data[entity] || 0) + amount;
      if (kp === 'DOET') {
        data['__DOET__'] = (data['__DOET__'] || 0) + amount;
      }
    }
  }
  // Debug: per locatie totalen
  for (const [loc, amt] of Object.entries(data)) {
    debug.perLocatie[loc] = round2(amt);
  }
  debug.totaalBedrag = round2(Object.values(data).reduce((s, v) => s + v, 0));
  return { data, stats: { total, unmapped }, warnings, debug };
}

/* ═══ TRAVIS PARSER (frontend) ═══ */

// Travis XLSX mapping: normalize location name → portaal entity
const TRAVIS_LOC_MAP = {
  "aalsmeer":"TW1 Aalsmeer BV", "amsterdam":"TW1 Amsterdam BV", "asten":"TW1 Asten BV",
  "bodegraven":"TW1 Bodegraven BV", "ede":"TW1 Ede BV", "eindhoven":"TW1 Eindhoven BV",
  "groenlo":"TW1 Groenlo BV", "hazeldonk":"TW1 Hazeldonk BV", "holten":"TW1 Holten BV",
  "maasvlakte":"TW1 Maasvlakte BV", "rilland":"TW1 Rilland BV", "roosendaal":"TW1 Roosendaal BV",
  "rotterdam":"TW1 Rotterdam BV", "steenwijk":"TW1 Steenwijk BV", "utrecht":"TW1 Utrecht BV",
  "venlo":"TW1 Venlo BV", "wehl":"TW1 Wehl BV", "doetinchem":"TW1 Groenlo BV|DOET",
};

function normalizeTravisLoc(raw) {
  let s = String(raw || "").trim();
  s = s.replace(/truckwash\s+1/gi, "").replace(/b\.?v\.?\s*$/i, "").replace(/\s+/g, " ").trim().toLowerCase();
  return TRAVIS_LOC_MAP[s] || null;
}

function parseTravisXlsx(fileBuffer) {
  /** Parse new Travis .xlsx format — one file, all locations, "Query result" sheet. */
  const wb = XLSX.read(fileBuffer, { type: 'array' });
  const sheetName = wb.SheetNames.find(n => n.toLowerCase().includes('query')) || wb.SheetNames[0];
  const rows = XLSX.utils.sheet_to_json(wb.Sheets[sheetName], { header: 1, defval: '' });
  if (!rows || rows.length < 2) return { data: {}, stats: { total: 0 }, warnings: ['Travis XLSX: geen data gevonden'] };

  const headers = rows[0].map(h => String(h).trim());
  // Find columns by header text
  const locIdx = headers.findIndex(h => h.toLowerCase().includes('location') && h.toLowerCase().includes('name'));
  const bedragIdx = headers.findIndex(h => h.toLowerCase().includes('location purchase price'));
  if (locIdx === -1 || bedragIdx === -1) {
    return { data: {}, stats: { total: 0 }, warnings: [`Travis XLSX: kolommen niet gevonden. Verwacht "Location → Name" en "Location Purchase Price". Headers: ${headers.slice(0, 10).join(', ')}`] };
  }

  const data = {};
  let total = 0;
  const unmapped = new Set();
  for (let i = 1; i < rows.length; i++) {
    const row = rows[i];
    if (!row || row.length === 0) continue;
    const amount = typeof row[bedragIdx] === 'number' ? row[bedragIdx] : parseFloat(row[bedragIdx]) || 0;
    if (amount === 0) continue;
    total++;
    const locRaw = String(row[locIdx] || "").trim();
    const mapped = normalizeTravisLoc(locRaw);
    if (mapped) {
      // Handle pipe-separated "TW1 Groenlo BV|DOET"
      const [entity, kp] = mapped.includes('|') ? mapped.split('|') : [mapped, ''];
      data[entity] = round2((data[entity] || 0) + amount);
      if (kp === 'DOET') {
        data['__TRAVIS_DOET__'] = round2((data['__TRAVIS_DOET__'] || 0) + amount);
      }
    } else if (locRaw) {
      unmapped.add(locRaw);
    }
  }
  const warnings = [];
  for (const loc of unmapped) warnings.push(`Travis: locatie '${loc}' niet gemapped`);
  return { data, stats: { total }, warnings };
}

function parseTravisFile(rowsRaw, fallbackLocation) {
  if (!rowsRaw || rowsRaw.length < 2) return { data: {}, stats: { total: 0 }, warnings: [] };
  const headers = rowsRaw[0].map(h => String(h).trim());
  const bedragIdx = findColIdx(headers, "bedrag_excl._btw", "bedrag_excl_btw", "bedrag_excl._btw", "vergoeding", "payment", "bedrag");
  const locIdx = findColIdx(headers, "naam_locatie", "naam locatie", "location", "locatie");
  if (bedragIdx === -1) return { data: {}, stats: { total: 0 }, warnings: [`Travis: bedrag-kolom niet gevonden. Headers: ${headers.join(', ')}`] };

  const data = {};
  let total = 0;
  for (let i = 1; i < rowsRaw.length; i++) {
    const row = rowsRaw[i];
    if (!row || row.every(c => !c && c !== 0)) continue;
    const bedragRaw = row[bedragIdx] !== undefined ? String(row[bedragIdx]) : '';
    const amount = parseNL(bedragRaw);
    if (amount === 0) continue;
    total++;
    let locRaw = locIdx !== -1 ? String(row[locIdx]||"").trim() : "";
    let location = locRaw ? mapLocatie(locRaw) : null;
    if (!location && fallbackLocation) location = fallbackLocation;
    if (location) {
      data[location] = (data[location] || 0) + amount;
    }
  }
  return { data, stats: { total }, warnings: [] };
}

/* ═══ BEREKENINGSMODULE (frontend) ═══ */

function calculateMaandafsluiting({ ptw3Result, ptw4Data, travisData, batchKortingen, ts8Omzet, totaalGroup, travisUploaded, batchUploaded }) {
  const locResults = {};
  for (const loc of Object.keys(LOCATION_DATA)) {
    locResults[loc] = { ptw3_total: 0, ptw3_doorb: 0, ptw4: 0, travis: 0, doet_ptw3_total: 0, doet_ptw3_doorb: 0, doet_ptw4: 0, doet_travis: 0 };
  }

  // Vul PTW3 data
  if (ptw3Result?.data) {
    for (const [loc, d] of Object.entries(ptw3Result.data)) {
      if (locResults[loc]) {
        locResults[loc].ptw3_total = d.bedrag;
        locResults[loc].ptw3_doorb = d.bedragDoorb;
        locResults[loc].doet_ptw3_total = d.doet_bedrag || 0;
        locResults[loc].doet_ptw3_doorb = d.doet_bedragDoorb || 0;
      }
    }
  }

  // Vul PTW4 data
  if (ptw4Data) {
    for (const [loc, amt] of Object.entries(ptw4Data)) {
      if (loc === '__DOET__') continue; // skip the DOET tracker
      if (locResults[loc]) locResults[loc].ptw4 = amt;
    }
    // Doetinchem PTW4 sub-stream under Groenlo
    if (ptw4Data['__DOET__'] && locResults['TW1 Groenlo BV']) {
      locResults['TW1 Groenlo BV'].doet_ptw4 = ptw4Data['__DOET__'];
    }
  }

  // Vul Travis data
  if (travisData) {
    for (const [loc, amt] of Object.entries(travisData)) {
      if (loc === '__TRAVIS_DOET__') continue;
      if (locResults[loc]) locResults[loc].travis = amt;
    }
    // Doetinchem Travis sub-stream under Groenlo
    if (travisData['__TRAVIS_DOET__'] && locResults['TW1 Groenlo BV']) {
      locResults['TW1 Groenlo BV'].doet_travis = travisData['__TRAVIS_DOET__'];
    }
  }

  // Aandeel% basis: ptw3_doorb + ptw4 per locatie
  let totalLocOmzet = 0;
  for (const d of Object.values(locResults)) {
    totalLocOmzet += d.ptw3_doorb + d.ptw4;
  }

  const locations = [];
  let totalFee = 0, totalDoorbelasting = 0, totalOmzet = 0, totalKorting = 0;

  for (const [locName, data] of Object.entries(locResults)) {
    const locInfo = LOCATION_DATA[locName];
    const feePct = locInfo.fee_pct;
    const locOmzetShare = data.ptw3_doorb + data.ptw4;
    const aandeelPct = totalLocOmzet > 0 && locOmzetShare > 0 ? locOmzetShare / totalLocOmzet : 0;
    const locKorting = aandeelPct > 0 ? round2(batchKortingen?.[locName] || 0) : 0;

    let doorbelasting;
    if (totaalGroup > 0) {
      doorbelasting = round2(aandeelPct * totaalGroup);
    } else {
      doorbelasting = round2(locOmzetShare);
    }

    let extraOmzet = round2(data.travis);
    let feeBasis;
    if (locName === "Truckstop 8 BV") {
      if (ts8Omzet > 0) {
        feeBasis = data.ptw3_total + data.ptw4 + ts8Omzet;
        extraOmzet = round2(ts8Omzet);
      } else {
        feeBasis = Math.max(doorbelasting, 0);
        extraOmzet = 0;
      }
    } else {
      feeBasis = travisUploaded
        ? data.ptw3_total + data.ptw4 + data.travis
        : Math.max(doorbelasting, 0);
    }

    const fee = round2(feeBasis * feePct);
    const omzet = round2(locOmzetShare);

    if (omzet > 0 || doorbelasting !== 0 || fee > 0 || locKorting > 0) {
      const locData = {
        locatie: locName,
        ptw3_total: round2(data.ptw3_total),
        ptw3_doorbelasting: round2(data.ptw3_doorb),
        ptw4: round2(data.ptw4),
        travis: extraOmzet,
        omzet,
        aandeel_pct: round2(aandeelPct * 100),
        korting: locKorting,
        fee_basis: round2(feeBasis),
        fee_pct: feePct,
        fee,
        doorbelasting,
        netto: round2(doorbelasting - fee),
        ...locInfo,
      };
      // Doetinchem split for Groenlo (PTW3 + PTW4 + Travis)
      if (locName === 'TW1 Groenlo BV') {
        const doetDoorb = round2(data.doet_ptw3_doorb + data.doet_ptw4);
        const groenDoorb = round2(locData.doorbelasting - doetDoorb);
        const doetFeeBasis = round2(data.doet_ptw3_total + data.doet_ptw4 + data.doet_travis);
        const doetFee = round2(doetFeeBasis * feePct);
        const groenFee = round2(locData.fee - doetFee);
        locData.doet_doorbelasting = doetDoorb;
        locData.doet_fee = doetFee;
        locData.groen_doorbelasting = groenDoorb;
        locData.groen_fee = groenFee;
      }
      locations.push(locData);
      totalFee += fee;
      totalDoorbelasting += doorbelasting;
      totalOmzet += omzet;
      totalKorting += locKorting;
    }
  }

  locations.sort((a, b) => a.locatie.localeCompare(b.locatie));

  // Check for missing RC codes
  const rcWarnings = [];
  for (const loc of locations) {
    const info = LOCATION_DATA[loc.locatie];
    if (info && (!info.rc_group_loc || !info.rc_loc_group)) {
      rcWarnings.push(`Relatiecodes voor ${loc.locatie} ontbreken. Locatie- en Group-export bevatten lege relatiecodes.`);
    }
  }

  return {
    locations,
    totals: {
      omzet: round2(totalOmzet),
      korting: round2(totalKorting),
      fee: round2(totalFee),
      doorbelasting: round2(totalDoorbelasting),
      netto: round2(totalDoorbelasting - totalFee),
    },
    totaal_group: totaalGroup,
    ts8_omzet: ts8Omzet,
    travis_uploaded: travisUploaded,
    batch_uploaded: batchUploaded,
    rc_warnings: rcWarnings,
  };
}

/* ═══ UI COMPONENTEN ═══ */

function FileDropzone({ label, description, file, onFileChange, onRemove, accept, inputRef, testId, multiple }) {
  const [dragOver, setDragOver] = useState(false);
  const handleDragOver = useCallback((e) => { e.preventDefault(); e.stopPropagation(); setDragOver(true); }, []);
  const handleDragLeave = useCallback((e) => { e.preventDefault(); e.stopPropagation(); setDragOver(false); }, []);
  const handleDrop = useCallback((e) => { e.preventDefault(); e.stopPropagation(); setDragOver(false); const files = e.dataTransfer.files; if (multiple) { for (let i = 0; i < files.length; i++) onFileChange(files[i]); } else { if (files?.[0]) onFileChange(files[0]); } }, [onFileChange, multiple]);
  const handleInputChange = useCallback((e) => { const files = e.target.files; if (multiple) { for (let i = 0; i < files.length; i++) onFileChange(files[i]); } else { if (files?.[0]) onFileChange(files[0]); } if (inputRef?.current) inputRef.current.value = ''; }, [onFileChange, multiple, inputRef]);
  if (file) {
    return (
      <div className="tw1-card p-4" data-testid={testId}>
        <div className="flex items-start justify-between mb-2">
          <div><p className="text-sm font-semibold text-[#0b1220]">{label}</p><p className="text-[10px] text-[#526071] mt-0.5">{description}</p></div>
          <Badge variant="outline" className="bg-[#1e8c45]/8 text-[#1e8c45] border-[#1e8c45]/20 text-[10px] font-semibold">Geladen</Badge>
        </div>
        <div className="flex items-center justify-between bg-[#1e8c45]/5 rounded-xl px-3 py-2.5 border border-[#1e8c45]/10">
          <div className="flex items-center gap-2.5 min-w-0">
            <div className="w-7 h-7 rounded-lg bg-[#1e8c45]/10 flex items-center justify-center flex-shrink-0"><FileText className="w-3.5 h-3.5 text-[#1e8c45]" /></div>
            <div className="min-w-0"><p className="text-xs font-medium truncate">{file.name}</p><p className="text-[10px] text-[#526071]">{(file.size / 1024).toFixed(1)} KB</p></div>
          </div>
          <button onClick={onRemove} className="text-[#526071] hover:text-[#c0392b] transition-colors p-1 rounded-lg hover:bg-[#c0392b]/5" data-testid={`${testId}-remove-btn`}><Trash2 className="w-3.5 h-3.5" /></button>
        </div>
      </div>
    );
  }
  return (
    <div className="tw1-card p-4" data-testid={testId}>
      <p className="text-sm font-semibold text-[#0b1220] mb-0.5">{label}</p>
      <p className="text-[10px] text-[#526071] mb-2.5">{description}</p>
      <label onDragOver={handleDragOver} onDragLeave={handleDragLeave} onDrop={handleDrop}
        className={`flex flex-col items-center justify-center gap-1.5 px-3 py-5 border-2 border-dashed rounded-xl cursor-pointer transition-all duration-200 ${dragOver ? 'border-[#f5a800] bg-[#f5a800]/8 scale-[1.01]' : 'border-[#dde3ee] hover:border-[#f5a800] hover:bg-[#f5a800]/4'}`}>
        <div className={`w-9 h-9 rounded-xl flex items-center justify-center transition-colors ${dragOver ? 'bg-[#f5a800]/15' : 'bg-[#003366]/5'}`}>
          <Upload className={`w-4 h-4 transition-colors ${dragOver ? 'text-[#f5a800]' : 'text-[#003366]'}`} />
        </div>
        <div className="text-center"><span className="text-xs font-medium text-[#0b1220]">Sleep hierheen</span><span className="text-[10px] text-[#526071] block mt-0.5">of klik om te kiezen</span></div>
        <input ref={inputRef} type="file" accept={accept} multiple={!!multiple} className="hidden" onChange={handleInputChange} />
      </label>
    </div>
  );
}

function AmountInput({ label, value, onChange, placeholder, testId }) {
  return (
    <div className="flex-shrink-0 w-full sm:w-auto">
      <label className="text-[11px] font-medium text-[#526071] block mb-1.5">{label}</label>
      <div className="relative">
        <span className="absolute left-3 top-1/2 -translate-y-1/2 text-sm text-[#526071] font-mono">€</span>
        <input type="text" inputMode="decimal" value={value} onChange={e => onChange(e.target.value)} placeholder={placeholder || '0,00'}
          className="h-9 w-full sm:w-[200px] rounded-lg border border-[#dde3ee] bg-white pl-8 pr-3 text-sm font-mono focus:outline-none focus:ring-2 focus:ring-[#f5a800]/40 focus:border-[#f5a800] transition-shadow"
          data-testid={testId} />
      </div>
    </div>
  );
}

/* ─── Excel Export Helpers (Fase 5) ─── */

function buildLocatiesRows(locations, locDetails, boekjaar, periode, betalingsconditie) {
  const fd = lastDayOfMonth(Number(boekjaar), Number(periode));
  const kopregel = `TW Termijnfactuur ${periode}-${boekjaar}`;
  const rows = [];
  locations.forEach(loc => {
    if (loc.doorbelasting <= 0 && loc.fee <= 0) return;
    const det = locDetails[loc.locatie] || {};
    const rc = det.rc_group_loc || loc.rc_group_loc || '';
    if (loc.locatie === 'TW1 Groenlo BV' && (loc.doet_doorbelasting || loc.doet_fee)) {
      // Groenlo split: 5 regels
      const groenOmzet = Math.round((loc.groen_doorbelasting || 0) * 100) / 100;
      const doetOmzet = Math.round((loc.doet_doorbelasting || 0) * 100) / 100;
      const groenFee = Math.round(-(loc.groen_fee || 0) * 100) / 100;
      const doetFee = Math.round(-(loc.doet_fee || 0) * 100) / 100;
      rows.push([rc, '75', String(boekjaar), String(periode), fd, 'EUR', betalingsconditie, kopregel, '8010', 'Omzet PTW', '01', groenOmzet, 1, '']);
      rows.push([rc, '75', String(boekjaar), String(periode), fd, 'EUR', betalingsconditie, kopregel, '8010', 'Omzet PTW', '01', doetOmzet, 1, 'DOET']);
      rows.push([rc, '75', String(boekjaar), String(periode), fd, 'EUR', betalingsconditie, kopregel, '5077', 'Fee', '01', groenFee, 1, '']);
      rows.push([rc, '75', String(boekjaar), String(periode), fd, 'EUR', betalingsconditie, kopregel, '5077', 'Fee', '01', doetFee, 1, 'DOET']);
      rows.push([rc, '75', String(boekjaar), String(periode), fd, 'EUR', betalingsconditie, kopregel, '1482HOLD', 'Afboeking RC Holding', '01', Math.round(-loc.netto * 100) / 100, 1, '']);
    } else {
      // Standaard 3 regels + lege kostenplaats
      rows.push([rc, '75', String(boekjaar), String(periode), fd, 'EUR', betalingsconditie, kopregel, '8010', 'Omzet PTW', '01', Math.round(loc.doorbelasting * 100) / 100, 1, '']);
      rows.push([rc, '75', String(boekjaar), String(periode), fd, 'EUR', betalingsconditie, kopregel, '5077', 'Fee', '01', Math.round(-loc.fee * 100) / 100, 1, '']);
      rows.push([rc, '75', String(boekjaar), String(periode), fd, 'EUR', betalingsconditie, kopregel, '1482HOLD', 'Afboeking RC Holding', '01', Math.round(-loc.netto * 100) / 100, 1, '']);
    }
  });
  return rows;
}

function buildGroupRows(locations, locDetails, boekjaar, periode, betalingsconditie) {
  const fd = lastDayOfMonth(Number(boekjaar), Number(periode));
  const kopregel = `TW Termijnfactuur ${periode}-${boekjaar}`;
  const grouped = {};
  locations.forEach(loc => {
    if (loc.doorbelasting <= 0 && loc.fee <= 0) return;
    const det = locDetails[loc.locatie] || {};
    const rc = det.rc_loc_group || loc.rc_loc_group || '';
    if (!grouped[rc]) grouped[rc] = [];
    grouped[rc].push(loc);
  });
  const rows = [];
  Object.keys(grouped).sort().forEach(rc => {
    grouped[rc].forEach(loc => {
      const det = locDetails[loc.locatie] || {};
      const db = loc.locatie === 'TW1 Rilland BV' ? '75' : '65';
      const afboekRek = loc.locatie === 'TW1 Bodegraven BV' ? '1482BODE' : '1482HOLD';
      const locLabel = loc.locatie.replace(' BV', '').replace('TW1 ', '');
      const netto = Math.round((loc.doorbelasting - loc.fee) * 100) / 100;
      rows.push([rc, db, String(boekjaar), String(periode), fd, 'EUR', betalingsconditie, kopregel, '8010', 'Omzet PTW', '01', Math.round(loc.doorbelasting * 100) / 100, 1, 'TW1G']);
      rows.push([rc, db, String(boekjaar), String(periode), fd, 'EUR', betalingsconditie, kopregel, '6077', 'Fee opbrengst', '01', Math.round(-loc.fee * 100) / 100, 1, 'TW1G']);
      rows.push([rc, db, String(boekjaar), String(periode), fd, 'EUR', betalingsconditie, kopregel, afboekRek, `Afboeking RC ${locLabel}`, '01', Math.round(-netto * 100) / 100, 1, 'TW1G']);
    });
  });
  return rows;
}

function buildHoldingRows(locations, locDetails, boekjaar, periode) {
  const fd = lastDayOfMonth(Number(boekjaar), Number(periode));
  const kopregel = `TW Termijnfactuur ${periode}-${boekjaar}`;
  const rows = [];
  locations.forEach(loc => {
    if (loc.netto === 0) return;
    const det = locDetails[loc.locatie] || {};
    const gb = det.gb_holding || loc.gb_holding || '';
    rows.push(['2', '60', String(boekjaar), String(periode), fd, kopregel, gb, `Afboeking ${loc.locatie}`, '01', Math.round(-loc.netto * 100) / 100, 1]);
    rows.push(['2', '60', String(boekjaar), String(periode), fd, kopregel, '1482GROU', `Afboeking ${loc.locatie}`, '01', Math.round(loc.netto * 100) / 100, 1]);
  });
  return rows;
}

// Download Excel — Sheet "Doorbelasting Generator" met kolombreedte
function downloadExcel(sheetData, headers, filename) {
  const wb = XLSX.utils.book_new();
  const ws = XLSX.utils.aoa_to_sheet([headers, ...sheetData]);
  // Column widths (14 for Group with Kostenplaats, others use first 13)
  ws['!cols'] = [14, 4, 4, 3, 12, 4, 6, 30, 16, 30, 6, 12, 6, 8].map(w => ({ wch: w }));
  XLSX.utils.book_append_sheet(wb, ws, 'Doorbelasting Generator');
  const b64 = XLSX.write(wb, { bookType: 'xls', type: 'base64' });
  const a = document.createElement('a');
  a.href = `data:application/vnd.ms-excel;base64,${b64}`;
  a.download = filename;
  a.click();
  return b64;
}

const LOC_CODE = {
  "TW1 Aalsmeer BV":"AALS","TW1 Amsterdam BV":"AMST","TW1 Asten BV":"ASTE",
  "TW1 Bodegraven BV":"BODE","TW1 Ede BV":"EDE","TW1 Eindhoven BV":"EIND",
  "TW1 Groenlo BV":"GROE","TW1 Holten BV":"HOLT","TW1 Maasvlakte BV":"MAAS",
  "TW1 Rilland BV":"RILL","TW1 Roosendaal BV":"ROOS","TW1 Rotterdam BV":"ROTT",
  "TW1 Steenwijk BV":"STEE","TW1 Utrecht BV":"UTRE","TW1 Venlo BV":"VENL",
  "TW1 Wehl BV":"WEHL","TW1 Hazeldonk BV":"HAZE","Truckstop 8 BV":"ACHT",
};

function buildSingleLocatieRows(loc, locDetails, boekjaar, periode, betalingsconditie) {
  const fd = lastDayOfMonth(Number(boekjaar), Number(periode));
  const kopregel = `TW Termijnfactuur ${periode}-${boekjaar}`;
  const det = locDetails[loc.locatie] || {};
  const rc = det.rc_group_loc || loc.rc_group_loc || '';
  if (loc.locatie === 'TW1 Groenlo BV' && (loc.doet_doorbelasting || loc.doet_fee)) {
    const groenOmzet = Math.round((loc.groen_doorbelasting || 0) * 100) / 100;
    const doetOmzet = Math.round((loc.doet_doorbelasting || 0) * 100) / 100;
    const groenFee = Math.round(-(loc.groen_fee || 0) * 100) / 100;
    const doetFee = Math.round(-(loc.doet_fee || 0) * 100) / 100;
    return [
      [rc, '75', String(boekjaar), String(periode), fd, 'EUR', betalingsconditie, kopregel, '8010', 'Omzet PTW', '01', groenOmzet, 1, ''],
      [rc, '75', String(boekjaar), String(periode), fd, 'EUR', betalingsconditie, kopregel, '8010', 'Omzet PTW', '01', doetOmzet, 1, 'DOET'],
      [rc, '75', String(boekjaar), String(periode), fd, 'EUR', betalingsconditie, kopregel, '5077', 'Fee', '01', groenFee, 1, ''],
      [rc, '75', String(boekjaar), String(periode), fd, 'EUR', betalingsconditie, kopregel, '5077', 'Fee', '01', doetFee, 1, 'DOET'],
      [rc, '75', String(boekjaar), String(periode), fd, 'EUR', betalingsconditie, kopregel, '1482HOLD', 'Afboeking RC Holding', '01', Math.round(-loc.netto * 100) / 100, 1, ''],
    ];
  }
  return [
    [rc, '75', String(boekjaar), String(periode), fd, 'EUR', betalingsconditie, kopregel, '8010', 'Omzet PTW', '01', Math.round(loc.doorbelasting * 100) / 100, 1, ''],
    [rc, '75', String(boekjaar), String(periode), fd, 'EUR', betalingsconditie, kopregel, '5077', 'Fee', '01', Math.round(-loc.fee * 100) / 100, 1, ''],
    [rc, '75', String(boekjaar), String(periode), fd, 'EUR', betalingsconditie, kopregel, '1482HOLD', 'Afboeking RC Holding', '01', Math.round(-loc.netto * 100) / 100, 1, ''],
  ];
}

const H_LOC = ['Relatiecode','Code dagboek','Boekjaar','Periode','Factuurdatum','Valuta','Code betalingsconditie','Omschrijving: Kopregel','Grootboekrekening','Omschrijving','Btw-code','Bedrag','Aantal','Kostenplaats'];
const H_GRP = ['Relatiecode','Code dagboek','Boekjaar','Periode','Factuurdatum','Valuta','Code betalingsconditie','Omschrijving: Kopregel','Grootboekrekening','Omschrijving','Btw-code','Bedrag','Aantal','Kostenplaats'];
const H_HLD = ['Relatiecode','Code dagboek','Boekjaar','Periode','Factuurdatum','Omschrijving: Kopregel','Grootboekrekening','Omschrijving','Btw-code','Bedrag','Aantal'];

const STEPS = [
  { key: 'berekenen', label: 'Berekenen', desc: 'Upload & omzet berekenen' },
  { key: 'exporteren', label: 'Exporteren', desc: 'Exact Online exports' },
];

export default function MaandafsluitingPage() {
  const now = new Date();
  const [step, setStep] = useState(0);
  const [selectedYear, setSelectedYear] = useState(now.getFullYear());
  const [selectedMonth, setSelectedMonth] = useState(now.getMonth() + 1);
  const [ptw3File, setPtw3File] = useState(null);
  const [ptw4File, setPtw4File] = useState([]);
  const [travisFiles, setTravisFiles] = useState([]);
  const [batchFile, setBatchFile] = useState([]);
  const [ts8Omzet, setTs8Omzet] = useState('');
  const [totaalGroup, setTotaalGroup] = useState('');
  const [results, setResults] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [showStats, setShowStats] = useState(false);
  const [exportPreview, setExportPreview] = useState('locaties');
  const [parseInfo, setParseInfo] = useState(null);
  const [showPtw4Debug, setShowPtw4Debug] = useState(false);
  const [exportPeriode, setExportPeriode] = useState(now.getMonth() + 1);
  const [exportBoekjaar, setExportBoekjaar] = useState(String(now.getFullYear()));
  const [exportBetalingsconditie, setExportBetalingsconditie] = useState('0');
  const [locDetails, setLocDetails] = useState({});
  const ptw3Ref = useRef(null);
  const ptw4Ref = useRef(null);
  const travisRef = useRef(null);
  const batchRef = useRef(null);

  // Sync export settings when month/year changes in header
  useEffect(() => {
    setExportPeriode(selectedMonth);
    setExportBoekjaar(String(selectedYear));
  }, [selectedMonth, selectedYear]);

  // Fetch location details from API when entering export step
  useEffect(() => {
    if (step === 1 && results) {
      api.get('/api/settings/locations/details')
        .then(res => {
          const map = {};
          (res.data.locations || []).forEach(loc => { map[loc.naam] = loc; });
          setLocDetails(map);
        })
        .catch(() => {
          // Fallback: use data from LOCATION_DATA already in results
          const map = {};
          results.locations.forEach(loc => {
            map[loc.locatie] = {
              rc_group_loc: loc.rc_group_loc,
              rc_loc_group: loc.rc_loc_group,
              gb_holding: loc.gb_holding,
              fee_pct: loc.fee_pct,
            };
          });
          setLocDetails(map);
        });
    }
  }, [step, results]);

  const parseDutchAmount = (str) => parseFloat(String(str).replace(/\./g, '').replace(',', '.')) || 0;

  const handleCalculate = async () => {
    if (!ptw3File && ptw4File.length === 0 && travisFiles.length === 0 && batchFile.length === 0) { toast.error('Upload minimaal één bestand'); return; }
    const groupVal = parseDutchAmount(totaalGroup);
    if (groupVal <= 0) { toast.error('Vul "Totaal gefactureerd door Group" in (verplicht)'); return; }
    setLoading(true); setError(''); setResults(null); setParseInfo(null);
    try {
      // ═══ ALLE PARSING IN FRONTEND ═══
      let ptw3Result = { data: {}, stats: { totalAll: 0, totalDoorb: 0, excluded: 0, uniqueOrders: 0 } };
      let ptw4Result = { data: {}, stats: { total: 0, unmapped: 0 }, warnings: [] };
      let batchKortingen = {};
      let batchStats = {};
      let allWarnings = [];

      // PTW3 — frontend parsing met user's exacte code
      if (ptw3File) {
        const rows = await readFileAsRows(ptw3File);
        ptw3Result = parseConsolidatedOrders(rows);
        if (ptw3Result.errors) allWarnings.push(...ptw3Result.errors);
      }

      // PTW4 — meerdere bestanden, frontend parsing, merge per locatie
      if (ptw4File.length > 0) {
        for (const f of ptw4File) {
          try {
            // Preprocessing: strip outer quotes van Exact Online exports
            const rawText = await f.text();
            const cleanedText = preprocessPTW4Text(rawText);
            const cleanedBlob = new Blob([cleanedText], { type: 'text/csv' });
            cleanedBlob.name = f.name;
            const rows = await readFileAsRows(cleanedBlob);
            const result = parsePTW4Orders(rows);
            for (const [loc, amt] of Object.entries(result.data)) {
              ptw4Result.data[loc] = (ptw4Result.data[loc] || 0) + amt;
            }
            ptw4Result.stats.total += result.stats?.total || 0;
            ptw4Result.stats.unmapped += result.stats?.unmapped || 0;
            if (result.warnings?.length) {
              allWarnings.push(...result.warnings.map(w => `PTW4 "${f.name}" — ${w}`));
            }
          } catch (e) {
            allWarnings.push(`PTW4 "${f.name}" — fout: ${e.message}`);
          }
        }
      }

      // Travis — nieuw: .xlsx met alle locaties, oud: losse CSV's per locatie
      let travisData = {};
      let travisTotal = 0;
      if (travisFiles.length > 0) {
        for (const file of travisFiles) {
          const isXlsx = /\.xlsx?$/i.test(file.name);
          if (isXlsx) {
            // Nieuw formaat: één xlsx met alle locaties
            try {
              const buf = await file.arrayBuffer();
              const result = parseTravisXlsx(buf);
              for (const [loc, amt] of Object.entries(result.data)) {
                if (loc === '__TRAVIS_DOET__') continue;
                travisData[loc] = round2((travisData[loc] || 0) + amt);
              }
              if (result.data['__TRAVIS_DOET__']) {
                travisData['__TRAVIS_DOET__'] = round2((travisData['__TRAVIS_DOET__'] || 0) + result.data['__TRAVIS_DOET__']);
              }
              travisTotal += result.stats.total;
              if (result.warnings?.length) allWarnings.push(...result.warnings);
            } catch (e) {
              allWarnings.push(`Travis "${file.name}" — fout: ${e.message}`);
            }
          } else {
            // Oud formaat: CSV per locatie (fallback)
            const rows = await readFileAsRows(file);
            const locFromName = file.name.replace(/\.csv$/i, "").split(/[/\\]/).pop().trim();
            const fallbackLoc = mapLocatie(locFromName);
            const result = parseTravisFile(rows, fallbackLoc);
            for (const [loc, amt] of Object.entries(result.data)) {
              travisData[loc] = (travisData[loc] || 0) + amt;
            }
            travisTotal += result.stats.total;
            if (result.warnings?.length) allWarnings.push(...result.warnings);
          }
        }
      }

      // Batch PDF — meerdere bestanden, backend (pdfplumber), merge kortingen per locatie
      if (batchFile.length > 0) {
        for (const f of batchFile) {
          try {
            const formData = new FormData();
            formData.append('batch_file', f);
            const batchRes = await api.post('/api/maandafsluiting/parse-batch', formData, { headers: { 'Content-Type': 'multipart/form-data' }, timeout: 180000, maxContentLength: 50 * 1024 * 1024, maxBodyLength: 50 * 1024 * 1024 });
            const filekortingen = batchRes.data.kortingen || {};
            for (const [loc, amt] of Object.entries(filekortingen)) {
              batchKortingen[loc] = (batchKortingen[loc] || 0) + amt;
            }
            const fileStats = batchRes.data.stats || {};
            batchStats.matched = (batchStats.matched || 0) + (fileStats.matched || 0);
            if (batchRes.data.warnings?.length) {
              allWarnings.push(...batchRes.data.warnings.map(w => `Batch "${f.name}" — ${w}`));
            }
          } catch (batchErr) {
            allWarnings.push(`Batch "${f.name}" — fout: ${batchErr.response?.data?.detail || batchErr.message}`);
          }
        }
      }

      // ═══ BEREKENING IN FRONTEND ═══
      const calcResult = calculateMaandafsluiting({
        ptw3Result,
        ptw4Data: ptw4Result.data,
        travisData,
        batchKortingen,
        ts8Omzet: parseDutchAmount(ts8Omzet),
        totaalGroup: groupVal,
        travisUploaded: travisFiles.length > 0,
        batchUploaded: batchFile.length > 0,
      });

      // Stats en warnings toevoegen
      calcResult.stats = {
        ptw3_orders: ptw3Result.stats?.uniqueOrders || 0,
        ptw3_total: round2(ptw3Result.stats?.totalAll || 0),
        ptw3_doorb: round2(ptw3Result.stats?.totalDoorb || 0),
        ptw3_excluded: ptw3Result.stats?.excluded || 0,
        ptw4_rows: ptw4Result.stats?.total || 0,
        ptw4_unmapped: ptw4Result.stats?.unmapped || 0,
        travis_rows: travisTotal,
        batch_matched: batchStats.blocks_matched || 0,
      };
      calcResult.warnings = allWarnings;

      // Parse info opslaan voor debug
      setParseInfo({
        ptw3: ptw3Result,
        ptw4: ptw4Result,
        travisData,
      });

      setResults(calcResult);
      toast.success(`Berekening voltooid: ${calcResult.locations.length} locaties verwerkt`);
    } catch (err) {
      console.error('[Maandafsluiting] Fout:', err);
      const msg = err.message || 'Berekening mislukt';
      setError(msg); toast.error(msg);
    } finally { setLoading(false); }
  };

  const clearAll = () => {
    setPtw3File(null); setPtw4File([]); setTravisFiles([]); setBatchFile([]);
    setTs8Omzet(''); setTotaalGroup(''); setResults(null); setError(''); setStep(0); setParseInfo(null);
    [ptw3Ref, ptw4Ref, travisRef, batchRef].forEach(r => { if (r.current) r.current.value = ''; });
  };

  const logExport = async (fn, type, meta, fileB64) => {
    try { await api.post('/api/maandafsluiting/export', { filename: fn, type, metadata: meta, file_b64: fileB64 || null }); } catch(e) { console.error('[Export] Log failed:', e); }
  };

  const handleExportLocatie = (loc) => {
    const code = LOC_CODE[loc.locatie] || 'LOC';
    const rows = buildSingleLocatieRows(loc, locDetails, exportBoekjaar, exportPeriode, exportBetalingsconditie);
    const fn = `Maandafsluiting_${code}_${exportPeriode}_${exportBoekjaar}.xls`;
    const b64 = downloadExcel(rows, H_LOC, fn);
    logExport(fn, 'Locatie Verkoopboeking DB75', { locatie: loc.locatie, regels: 3, periode: exportPeriode, boekjaar: exportBoekjaar }, b64);
    toast.success(`Export: ${fn}`);
  };

  const handleExportAlleLocaties = async () => {
    if (!results?.locations?.length) return;
    const locs = results.locations.filter(l => l.doorbelasting > 0 || l.fee > 0);
    for (let i = 0; i < locs.length; i++) {
      handleExportLocatie(locs[i]);
      if (i < locs.length - 1) await new Promise(r => setTimeout(r, 400));
    }
    toast.success(`${locs.length} locatie-exports gedownload`);
  };

  const handleExportGroup = () => {
    if (!results?.locations?.length) return;
    const rows = buildGroupRows(results.locations, locDetails, exportBoekjaar, exportPeriode, exportBetalingsconditie);
    const fn = `Maandafsluiting_Group_${exportPeriode}_${exportBoekjaar}.csv`;
    // CSV UTF-8 export (kommagescheiden)
    const csvLines = [H_GRP.join(',')];
    rows.forEach(row => {
      csvLines.push(row.map(cell => {
        const s = String(cell);
        return s.includes(',') || s.includes('"') ? `"${s.replace(/"/g, '""')}"` : s;
      }).join(','));
    });
    const bom = '\uFEFF';
    const csvContent = bom + csvLines.join('\r\n');
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = fn; a.click();
    URL.revokeObjectURL(url);
    logExport(fn, 'Group Inkoopboeking DB65 (CSV)', { locaties: results.locations.length, regels: rows.length, periode: exportPeriode, boekjaar: exportBoekjaar }, btoa(csvContent));
    toast.success(`Export: ${fn}`);
  };
  const handleExportHolding = () => {
    if (!results?.locations?.length) return;
    const rows = buildHoldingRows(results.locations, locDetails, exportBoekjaar, exportPeriode);
    const fn = `Maandafsluiting_Holding_${exportPeriode}_${exportBoekjaar}.xls`;
    const b64h = downloadExcel(rows, H_HLD, fn);
    logExport(fn, 'Holding Inkoopboeking DB60', { locaties: results.locations.length, regels: rows.length, periode: exportPeriode, boekjaar: exportBoekjaar }, b64h);
    toast.success(`Export: ${fn}`);
  };

  const hasFiles = ptw3File || ptw4File.length > 0 || travisFiles.length > 0 || batchFile.length > 0;
  const locatiesRows = results ? buildLocatiesRows(results.locations, locDetails, exportBoekjaar, exportPeriode, exportBetalingsconditie) : [];
  const groupRows = results ? buildGroupRows(results.locations, locDetails, exportBoekjaar, exportPeriode, exportBetalingsconditie) : [];
  const holdingRows = results ? buildHoldingRows(results.locations, locDetails, exportBoekjaar, exportPeriode) : [];

  // Validation checks
  const holdingBalance = holdingRows.reduce((sum, row) => sum + (typeof row[9] === 'number' ? row[9] : 0), 0);
  const holdingBalanceOk = Math.abs(holdingBalance) < 0.01;
  const locOmzetSum = locatiesRows.filter(row => row[8] === '8010').reduce((s, row) => s + (typeof row[11] === 'number' ? row[11] : 0), 0);
  const groupOmzetSum = groupRows.filter((_, i) => i % 3 === 0).reduce((s, row) => s + (typeof row[11] === 'number' ? row[11] : 0), 0);
  const omzetMatchOk = Math.abs(locOmzetSum - groupOmzetSum) < 0.01;

  return (
    <div className="tw1-fade-in" data-testid="maandafsluiting-page">
      <HeroBanner title="Maandafsluiting" subtitle={`${MONTH_NAMES[selectedMonth - 1]} ${selectedYear} — ${step === 0 ? 'Omzet Bereken Model' : 'Doorbelasting Generator'}`} variant="default" icon={CalendarCheck}
        logos={[{ src: PTW_LOGO, alt: 'PTW' }, { src: TRAVIS_LOGO, alt: 'Travis' }, { src: EXACT_LOGO, alt: 'Exact' }]}
        actions={<div className="flex items-center gap-2">
          <select value={selectedMonth} onChange={(e) => setSelectedMonth(Number(e.target.value))} className="h-8 text-xs rounded-lg border border-gray-200 px-2 bg-white focus:outline-none focus:ring-2 focus:ring-[#f5a800]/40" data-testid="maandafsluiting-month-select">{MONTH_NAMES.map((m, i) => <option key={i} value={i + 1}>{m}</option>)}</select>
          <select value={selectedYear} onChange={(e) => setSelectedYear(Number(e.target.value))} className="h-8 text-xs rounded-lg border border-gray-200 px-2 bg-white focus:outline-none focus:ring-2 focus:ring-[#f5a800]/40" data-testid="maandafsluiting-year-select">{[2024, 2025, 2026, 2027].map(y => <option key={y} value={y}>{y}</option>)}</select>
        </div>}
      />

      {/* Stepper */}
      <div className="flex items-center gap-2 mb-6">
        {STEPS.map((s, i) => (
          <React.Fragment key={s.key}>
            <button onClick={() => { if (i === 0 || (i === 1 && results)) setStep(i); }} disabled={i === 1 && !results}
              className={`flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-medium transition-colors ${step === i ? 'bg-[#003366] text-white shadow-sm' : step > i ? 'bg-[#1e8c45]/10 text-[#1e8c45]' : 'bg-gray-100 text-[#526071]'} ${i === 1 && !results ? 'opacity-40 cursor-not-allowed' : ''}`}
              data-testid={`step-${s.key}`}>
              <span className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold ${step === i ? 'bg-white/20' : step > i ? 'bg-[#1e8c45]/20' : 'bg-gray-200'}`}>{step > i ? <Check className="w-3 h-3" /> : i + 1}</span>
              <span className="hidden sm:inline">{s.label}</span>
            </button>
            {i < STEPS.length - 1 && <ChevronRight className="w-4 h-4 text-gray-300" />}
          </React.Fragment>
        ))}
      </div>

      {/* STEP 0: Berekenen */}
      {step === 0 && (<>
        <div className="grid grid-cols-1 lg:grid-cols-5 gap-5 mb-6">
          <div className="lg:col-span-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
              <FileDropzone label="Orders PTW3" description="CSV, puntkomma-gescheiden" file={ptw3File} onFileChange={(f) => { setPtw3File(f); toast.info(`PTW3: ${f.name}`); }} onRemove={() => { setPtw3File(null); if (ptw3Ref.current) ptw3Ref.current.value=''; }} accept=".csv" inputRef={ptw3Ref} testId="dropzone-ptw3" />
              <FileDropzone label="Orders PTW4" description={"CSV · Meerdere bestanden\n(factuurperiode 1 & 2)"} file={ptw4File.length > 0 ? { name: `${ptw4File.length} bestand${ptw4File.length > 1 ? 'en' : ''}: ${ptw4File.map(f=>f.name).join(', ')}`, size: ptw4File.reduce((s,f) => s + f.size, 0) } : null} onFileChange={(f) => { setPtw4File(prev => [...prev, f]); toast.info(`PTW4: ${f.name}`); }} onRemove={() => { setPtw4File([]); if (ptw4Ref.current) ptw4Ref.current.value=''; }} accept=".csv" inputRef={ptw4Ref} testId="dropzone-ptw4" multiple />
              <FileDropzone label="Travis Omzet" description="Excel (.xlsx) of CSV per locatie" file={travisFiles.length > 0 ? { name: `${travisFiles.length} bestand${travisFiles.length > 1 ? 'en' : ''}: ${travisFiles.map(f=>f.name).join(', ')}`, size: travisFiles.reduce((s,f) => s + f.size, 0) } : null} onFileChange={(f) => { setTravisFiles(prev => [...prev, f]); toast.info(`Travis: ${f.name}`); }} onRemove={() => { setTravisFiles([]); if (travisRef.current) travisRef.current.value=''; }} accept=".xlsx,.xls,.csv" inputRef={travisRef} testId="dropzone-travis" multiple />
              <FileDropzone label="Batchrapporten" description={"PDF · Meerdere bestanden\n(deel 1 & deel 2)"} file={batchFile.length > 0 ? { name: `${batchFile.length} bestand${batchFile.length > 1 ? 'en' : ''}: ${batchFile.map(f=>f.name).join(', ')}`, size: batchFile.reduce((s,f) => s + f.size, 0) } : null} onFileChange={(f) => { setBatchFile(prev => [...prev, f]); toast.info(`Batch: ${f.name}`); }} onRemove={() => { setBatchFile([]); if (batchRef.current) batchRef.current.value=''; }} accept=".pdf" inputRef={batchRef} testId="dropzone-batch" multiple />
            </div>
            <div className="tw1-card p-4 mt-4 flex flex-col gap-4">
              <div className="flex flex-col sm:flex-row gap-4">
                <AmountInput label="Totaal gefactureerd door Group (uit Exact)" value={totaalGroup} onChange={setTotaalGroup} placeholder="bijv. 781.000,00" testId="totaal-group-input" />
                <AmountInput label="Truckstop 8 omzet (handmatig)" value={ts8Omzet} onChange={setTs8Omzet} testId="ts8-omzet-input" />
              </div>
              <div className="flex gap-2">
                <Button onClick={handleCalculate} disabled={loading || !hasFiles} className="tw1-btn bg-[#003366] hover:bg-[#00244d] text-white h-9 px-5" data-testid="calculate-btn">
                  {loading ? (<><span className="animate-spin mr-2 w-4 h-4 border-2 border-white/30 border-t-white rounded-full inline-block" />Berekenen...</>) : (<><Calculator className="w-4 h-4 mr-1.5" />Bereken Omzet</>)}
                </Button>
                {hasFiles && <Button variant="outline" onClick={clearAll} className="h-9 text-xs" data-testid="clear-btn"><Trash2 className="w-3.5 h-3.5 mr-1" />Wissen</Button>}
              </div>
            </div>
            {error && <div className="bg-[#c0392b]/5 border border-[#c0392b]/15 rounded-xl p-4 mt-4 text-sm text-[#c0392b] flex items-start gap-2" data-testid="error-message"><AlertTriangle className="w-4 h-4 flex-shrink-0 mt-0.5" /><span>{error}</span></div>}
          </div>
          <div className="space-y-4">
            <div className="tw1-card p-5">
              <h3 className="font-semibold text-sm text-[#0b1220] mb-3 flex items-center gap-2"><Info className="w-4 h-4 text-[#003366]" /> Formules</h3>
              <div className="space-y-3 text-xs text-[#526071]">
                <div><p className="font-medium text-[#0b1220] mb-0.5">Doorbelasting</p><p>Aandeel% &times; Totaal Group &minus; Korting</p></div>
                <div><p className="font-medium text-[#0b1220] mb-0.5">Aandeel%</p><p>Locatie omzet / Totaal omzet</p></div>
                <div><p className="font-medium text-[#0b1220] mb-0.5">Fee</p><p>(PTW3 + PTW4 + Travis) &times; <strong>11%</strong></p><p className="text-[10px] mt-0.5 flex items-center gap-1"><Badge variant="outline" className="text-[9px] border-[#f5a800]/30 text-[#f5a800] px-1 py-0">5%</Badge><span>Truckstop 8 BV</span></p></div>
                <div><p className="font-medium text-[#0b1220] mb-0.5">Batchkorting</p><p>Automatisch uit PDF</p></div>
              </div>
            </div>
            <div className="tw1-card p-5">
              <h3 className="font-semibold text-sm text-[#0b1220] mb-3">Exclusie-prefixes</h3>
              <div className="space-y-1">
                {['DKV','Multitankcard','Cargocard','Cargo Card','Contant','Pin','Travelcard'].map(p => (<div key={p} className="flex items-center gap-2"><span className="w-1.5 h-1.5 rounded-full bg-[#f5a800]" /><span className="text-xs text-[#526071]">Klant {p}</span></div>))}
                <p className="text-[10px] text-[#526071]/70 mt-2 pt-2 border-t border-gray-100">WEL fee, NIET doorbelasting/aandeel</p>
              </div>
            </div>
          </div>
        </div>

        {loading && <div className="space-y-4" data-testid="loading-skeleton"><div className="grid grid-cols-2 sm:grid-cols-5 gap-3">{[...Array(5)].map((_, i) => <Skeleton key={i} className="h-20 rounded-xl" />)}</div><Skeleton className="h-64 rounded-xl" /></div>}

        {results && !loading && (
          <div className="space-y-6 tw1-fade-in" data-testid="omzet-results">
            <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
              <div className="tw1-card p-4" data-testid="stat-locaties"><div className="flex items-center gap-2 mb-2"><div className="w-7 h-7 rounded-lg bg-[#003366]/8 flex items-center justify-center"><BarChart3 className="w-3.5 h-3.5 text-[#003366]" /></div></div><p className="text-2xl font-bold font-mono text-[#0b1220]">{results.locations.length}</p><p className="text-[10px] text-[#526071] mt-0.5">Locaties</p></div>
              <div className="tw1-card p-4" data-testid="stat-omzet"><div className="flex items-center gap-2 mb-2"><div className="w-7 h-7 rounded-lg bg-[#003366]/8 flex items-center justify-center"><TrendingUp className="w-3.5 h-3.5 text-[#003366]" /></div></div><p className="text-lg font-bold font-mono text-[#003366]">{formatEUR(results.totals.omzet)}</p><p className="text-[10px] text-[#526071] mt-0.5">Totale Omzet</p></div>
              <div className="tw1-card p-4" data-testid="stat-doorbelasting"><p className="text-lg font-bold font-mono text-[#1e8c45]">{formatEUR(results.totals.doorbelasting)}</p><p className="text-[10px] text-[#526071] mt-0.5">Doorbelasting</p></div>
              <div className="tw1-card p-4" data-testid="stat-fee"><p className="text-lg font-bold font-mono text-[#c0392b]">-{formatEUR(results.totals.fee)}</p><p className="text-[10px] text-[#526071] mt-0.5">Fee</p></div>
              <div className="tw1-card p-4" data-testid="stat-netto"><p className="text-lg font-bold font-mono text-[#0b1220]">{formatEUR(results.totals.netto)}</p><p className="text-[10px] text-[#526071] mt-0.5">Netto</p></div>
            </div>

            {results.stats && (
              <button onClick={() => setShowStats(!showStats)} className="flex items-center gap-2 text-xs text-[#526071] hover:text-[#0b1220] transition-colors" data-testid="toggle-stats-btn">
                {showStats ? <ChevronUp className="w-3.5 h-3.5" /> : <ChevronDown className="w-3.5 h-3.5" />}<span>Parsing statistieken</span>
                {results.batch_uploaded && <Badge variant="outline" className="text-[10px] border-[#1e8c45]/30 text-[#1e8c45]">Batchkorting uit PDF</Badge>}
              </button>
            )}
            {showStats && results.stats && (
              <div className="tw1-card p-4 grid grid-cols-2 sm:grid-cols-4 gap-4 text-xs" data-testid="parsing-stats">
                <div><p className="text-[#526071] mb-1">PTW3</p><p className="font-mono">{results.stats.ptw3_orders} unieke orders</p><p className="text-[10px] text-[#526071]">{formatEUR(results.stats.ptw3_total)} totaal, {results.stats.ptw3_excluded} exclusie</p></div>
                <div><p className="text-[#526071] mb-1">PTW4</p><p className="font-mono">{results.stats.ptw4_rows} regels</p><p className="text-[10px] text-[#526071]">{results.stats.ptw4_unmapped} niet gemapped</p></div>
                <div><p className="text-[#526071] mb-1">Travis</p><p className="font-mono">{results.stats.travis_rows} regels</p></div>
                <div><p className="text-[#526071] mb-1">Batchrapport</p><p className="font-mono">{results.stats.batch_matched||0} locaties</p></div>
              </div>
            )}

            {results.warnings?.length > 0 && (
              <div className="tw1-card p-4 border-l-4 border-[#f5a800]" data-testid="warnings-panel">
                <h3 className="font-semibold text-sm text-[#0b1220] mb-2 flex items-center gap-2"><AlertTriangle className="w-4 h-4 text-[#f5a800]" />{results.warnings.length} Waarschuwing{results.warnings.length > 1 ? 'en' : ''}</h3>
                <div className="space-y-1 max-h-32 overflow-y-auto">{results.warnings.map((w, i) => <p key={i} className="text-xs text-[#526071]">{w}</p>)}</div>
              </div>
            )}

            {results.rc_warnings?.length > 0 && (
              <div className="tw1-card p-4 border-l-4 border-[#f59e0b]" data-testid="rc-warnings-panel">
                <div className="space-y-1">{results.rc_warnings.map((w, i) => <p key={i} className="text-xs text-[#f59e0b] font-medium">{w}</p>)}</div>
              </div>
            )}

            <div className="tw1-card overflow-hidden" data-testid="results-table">
              <div className="p-4 border-b border-gray-100 flex items-center justify-between">
                <h3 className="font-semibold text-sm text-[#0b1220]">Resultaten per Locatie</h3>
                <div className="flex items-center gap-2">
                  <Badge variant="outline" className="text-[10px] text-[#526071]">Group: {formatEUR(results.totaal_group)}</Badge>
                  <Badge variant="outline" className="text-[10px] text-[#526071]">{results.locations.length} locaties</Badge>
                </div>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full tw1-table" data-testid="results-table-body">
                  <thead><tr className="bg-[#f6f7fb]">
                    <th className="px-3 py-3 text-left text-xs font-semibold text-[#526071] uppercase tracking-wider">Locatie</th>
                    <th className="px-3 py-3 text-right text-xs font-semibold text-[#526071] uppercase tracking-wider">Omzet</th>
                    <th className="px-3 py-3 text-right text-xs font-semibold text-[#526071] uppercase tracking-wider">Travis</th>
                    <th className="px-3 py-3 text-right text-xs font-semibold text-[#526071] uppercase tracking-wider">Aandeel%</th>
                    <th className="px-3 py-3 text-right text-xs font-semibold text-[#526071] uppercase tracking-wider">Doorbelasting</th>
                    <th className="px-3 py-3 text-right text-xs font-semibold text-[#526071] uppercase tracking-wider">Korting</th>
                    <th className="px-3 py-3 text-right text-xs font-semibold text-[#526071] uppercase tracking-wider">Fee</th>
                    <th className="px-3 py-3 text-right text-xs font-semibold text-[#526071] uppercase tracking-wider">Netto</th>
                  </tr></thead>
                  <tbody>
                    {results.locations.map((loc, i) => (
                      <tr key={i} className="border-b border-gray-50 hover:bg-[#003366]/[0.02] transition-colors" data-testid={`result-row-${i}`}>
                        <td className="px-3 py-3 text-sm font-medium text-[#0b1220]"><div className="flex items-center gap-2">{loc.locatie}{loc.fee_pct === 0.05 && <Badge variant="outline" className="text-[9px] border-[#f5a800]/30 text-[#f5a800]">5%</Badge>}</div></td>
                        <td className="px-3 py-3 text-sm font-mono text-right text-[#526071]">{formatEUR(loc.omzet)}</td>
                        <td className="px-3 py-3 text-sm font-mono text-right text-[#526071]">{loc.travis > 0 ? formatEUR(loc.travis) : '\u2014'}</td>
                        <td className="px-3 py-3 text-sm font-mono text-right text-[#526071]">{loc.aandeel_pct?.toFixed(2)}%</td>
                        <td className="px-3 py-3 text-sm font-mono text-right text-[#1e8c45]">{formatEUR(loc.doorbelasting)}</td>
                        <td className="px-3 py-3 text-sm font-mono text-right text-[#526071]">{loc.korting > 0 ? `-${formatEUR(loc.korting)}` : '\u2014'}</td>
                        <td className="px-3 py-3 text-sm font-mono text-right text-[#c0392b]">-{formatEUR(loc.fee)}</td>
                        <td className="px-3 py-3 text-sm font-mono text-right font-bold text-[#0b1220]">{formatEUR(loc.netto)}</td>
                      </tr>
                    ))}
                    <tr className="bg-[#003366]/[0.03] border-t-2 border-[#003366]/10" data-testid="result-row-totals">
                      <td className="px-3 py-3 text-sm font-bold text-[#0b1220]">TOTAAL</td>
                      <td className="px-3 py-3 text-sm font-mono text-right font-bold text-[#003366]">{formatEUR(results.totals.omzet)}</td>
                      <td className="px-3 py-3 text-sm font-mono text-right font-bold text-[#526071]">{formatEUR(results.locations.reduce((s, l) => s + (l.travis || 0), 0))}</td>
                      <td className="px-3 py-3 text-sm font-mono text-right font-bold text-[#526071]">100%</td>
                      <td className="px-3 py-3 text-sm font-mono text-right font-bold text-[#1e8c45]">{formatEUR(results.totals.doorbelasting)}</td>
                      <td className="px-3 py-3 text-sm font-mono text-right font-bold text-[#526071]">{results.totals.korting > 0 ? `-${formatEUR(results.totals.korting)}` : '\u2014'}</td>
                      <td className="px-3 py-3 text-sm font-mono text-right font-bold text-[#c0392b]">-{formatEUR(results.totals.fee)}</td>
                      <td className="px-3 py-3 text-sm font-mono text-right font-bold text-[#0b1220]">{formatEUR(results.totals.netto)}</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>

            {/* PTW4 Debug Panel */}
            {parseInfo?.ptw4?.debug && parseInfo.ptw4.debug.headers.length > 0 && (
              <div className="tw1-card overflow-hidden" data-testid="ptw4-debug-panel">
                <button onClick={() => setShowPtw4Debug(!showPtw4Debug)} className="w-full flex items-center justify-between px-4 py-3 text-xs font-medium text-[#526071] hover:text-[#0b1220] hover:bg-gray-50 transition-colors">
                  <span className="flex items-center gap-2">
                    {showPtw4Debug ? <ChevronUp className="w-3.5 h-3.5" /> : <ChevronDown className="w-3.5 h-3.5" />}
                    PTW4 Debug Info
                  </span>
                  <span className="font-mono text-[10px]">bedrag kolom: [{parseInfo.ptw4.debug.bedragIdx}] {parseInfo.ptw4.debug.bedragNaam} | klant kolom: [{parseInfo.ptw4.debug.klantIdx}] {parseInfo.ptw4.debug.klantNaam}</span>
                </button>
                {showPtw4Debug && (
                  <div className="px-4 pb-4 space-y-4 text-xs border-t border-gray-100 pt-3">
                    {/* 1. Kolommen */}
                    <div>
                      <p className="font-semibold text-[#0b1220] mb-1">Alle kolomnamen ({parseInfo.ptw4.debug.headers.length} kolommen)</p>
                      <div className="font-mono bg-gray-50 rounded-lg p-2 text-[10px] space-y-0.5">
                        {parseInfo.ptw4.debug.headers.map((h, i) => (
                          <span key={i} className={`inline-block mr-2 px-1 rounded ${i === parseInfo.ptw4.debug.bedragIdx ? 'bg-[#c0392b]/15 text-[#c0392b] font-bold' : i === parseInfo.ptw4.debug.klantIdx ? 'bg-[#003366]/15 text-[#003366] font-bold' : ''}`}>
                            [{i}]{h}
                          </span>
                        ))}
                      </div>
                      <div className="flex gap-4 mt-1 text-[10px]">
                        <span className="text-[#003366] font-semibold">Klant: [{parseInfo.ptw4.debug.klantIdx}] {parseInfo.ptw4.debug.klantNaam}</span>
                        <span className="text-[#c0392b] font-semibold">Bedrag: [{parseInfo.ptw4.debug.bedragIdx}] {parseInfo.ptw4.debug.bedragNaam}</span>
                      </div>
                    </div>

                    {/* 2. Eerste 10 rijen — ALLE kolommen */}
                    <div>
                      <p className="font-semibold text-[#0b1220] mb-1">Eerste {parseInfo.ptw4.debug.rows.length} rijen — alle kolommen (ruwe waarden)</p>
                      <div className="overflow-x-auto max-h-[400px] overflow-y-auto">
                        <table className="w-full text-[10px] font-mono border-collapse">
                          <thead className="sticky top-0 bg-gray-100">
                            <tr>
                              <th className="px-1.5 py-1 text-left border-b border-gray-200">Rij</th>
                              {parseInfo.ptw4.debug.headers.map((h, i) => (
                                <th key={i} className={`px-1.5 py-1 text-left border-b border-gray-200 whitespace-nowrap ${i === parseInfo.ptw4.debug.bedragIdx ? 'bg-[#c0392b]/10' : i === parseInfo.ptw4.debug.klantIdx ? 'bg-[#003366]/10' : ''}`}>
                                  [{i}]{h}
                                </th>
                              ))}
                            </tr>
                          </thead>
                          <tbody>
                            {parseInfo.ptw4.debug.rows.map((r, ri) => (
                              <tr key={ri} className="border-b border-gray-50 hover:bg-gray-50/50">
                                <td className="px-1.5 py-1 text-gray-400">{r.rij}</td>
                                {r.alleCellen.map((c, ci) => (
                                  <td key={ci} className={`px-1.5 py-1 max-w-[200px] truncate ${ci === parseInfo.ptw4.debug.bedragIdx ? 'bg-[#c0392b]/5 font-bold' : ci === parseInfo.ptw4.debug.klantIdx ? 'bg-[#003366]/5 font-bold' : ''}`}>
                                    {c.waarde || '\u2014'}
                                  </td>
                                ))}
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>

                    {/* 3. Per rij: bedrag raw → parsed → locatie */}
                    <div>
                      <p className="font-semibold text-[#0b1220] mb-1">Per rij: bedrag parsing + locatie mapping</p>
                      <div className="overflow-x-auto">
                        <table className="w-full text-[10px] font-mono">
                          <thead><tr className="bg-gray-50">
                            <th className="px-2 py-1 text-left">Rij</th>
                            <th className="px-2 py-1 text-left">Klant (raw)</th>
                            <th className="px-2 py-1 text-left">Bedrag (raw)</th>
                            <th className="px-2 py-1 text-right">Geparsed</th>
                            <th className="px-2 py-1 text-left">Locatie</th>
                          </tr></thead>
                          <tbody>{parseInfo.ptw4.debug.rows.map((r, i) => (
                            <tr key={i} className={`border-b border-gray-50 ${r.bedragParsed > 10000 ? 'bg-[#c0392b]/5' : ''}`}>
                              <td className="px-2 py-1 text-gray-400">{r.rij}</td>
                              <td className="px-2 py-1 max-w-[200px] truncate">{r.klantRaw}</td>
                              <td className="px-2 py-1">"{r.bedragRaw}"</td>
                              <td className={`px-2 py-1 text-right font-bold ${r.bedragParsed > 10000 ? 'text-[#c0392b]' : ''}`}>{r.bedragParsed.toLocaleString('nl-NL', {minimumFractionDigits:2})}</td>
                              <td className="px-2 py-1">{r.locatie}</td>
                            </tr>
                          ))}</tbody>
                        </table>
                      </div>
                    </div>

                    {/* 4. Totaal per locatie */}
                    <div>
                      <p className="font-semibold text-[#0b1220] mb-1">PTW4 totaal per locatie</p>
                      <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                        {Object.entries(parseInfo.ptw4.debug.perLocatie).sort(([a],[b]) => a.localeCompare(b)).map(([loc, amt]) => (
                          <div key={loc} className={`bg-gray-50 rounded-lg p-2 ${amt > 50000 ? 'border-2 border-[#c0392b]/30 bg-[#c0392b]/5' : ''}`}>
                            <p className="text-[#526071] text-[10px]">{loc}</p>
                            <p className={`font-mono font-bold ${amt > 50000 ? 'text-[#c0392b]' : ''}`}>{formatEUR(amt)}</p>
                          </div>
                        ))}
                      </div>
                      <div className="mt-2 bg-gray-100 rounded-lg p-2 flex justify-between">
                        <span className="font-semibold">TOTAAL PTW4</span>
                        <span className={`font-mono font-bold ${(parseInfo.ptw4.debug.totaalBedrag || 0) > 100000 ? 'text-[#c0392b]' : ''}`}>{formatEUR(parseInfo.ptw4.debug.totaalBedrag)}</span>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            )}

            <div className="flex items-center justify-between">
              <Button variant="ghost" size="sm" onClick={clearAll} className="text-xs" data-testid="clear-results-btn"><Trash2 className="w-3 h-3 mr-1" /> Opnieuw beginnen</Button>
              <Button onClick={() => setStep(1)} className="tw1-btn bg-[#003366] hover:bg-[#00244d] text-white h-9 px-5" data-testid="goto-export-btn">Naar Exports <ChevronRight className="w-4 h-4 ml-1" /></Button>
            </div>
          </div>
        )}

        {!results && !loading && !hasFiles && (
          <div className="tw1-card p-12 text-center" data-testid="empty-state">
            <div className="w-16 h-16 rounded-2xl bg-[#003366]/5 flex items-center justify-center mx-auto mb-4"><Calculator className="w-8 h-8 text-[#003366]/40" /></div>
            <h3 className="text-lg font-semibold text-[#0b1220] mb-2">Omzet Bereken Model</h3>
            <p className="text-sm text-[#526071] max-w-md mx-auto">Upload PTW3, PTW4, Travis en/of Batchrapporten om de omzet, doorbelasting en fee per locatie te berekenen.</p>
          </div>
        )}
      </>)}

      {/* STEP 1: Exporteren */}
      {step === 1 && results && (
        <div className="space-y-6 tw1-fade-in" data-testid="export-step">
          {/* Export invoervelden */}
          <div className="tw1-card p-6">
            <h3 className="text-lg font-bold text-[#0b1220] mb-4">Export instellingen</h3>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              <div>
                <label className="text-xs font-medium text-[#526071] block mb-1.5">Periode</label>
                <select value={exportPeriode} onChange={e => setExportPeriode(Number(e.target.value))}
                  className="h-9 w-full rounded-lg border border-[#dde3ee] bg-white px-3 text-sm focus:outline-none focus:ring-2 focus:ring-[#f5a800]/40 focus:border-[#f5a800] transition-shadow"
                  data-testid="export-periode-select">
                  {MONTH_NAMES.map((m, i) => <option key={i} value={i + 1}>{i + 1} — {m}</option>)}
                </select>
              </div>
              <div>
                <label className="text-xs font-medium text-[#526071] block mb-1.5">Boekjaar</label>
                <input type="text" value={exportBoekjaar} onChange={e => setExportBoekjaar(e.target.value)}
                  className="h-9 w-full rounded-lg border border-[#dde3ee] bg-white px-3 text-sm font-mono focus:outline-none focus:ring-2 focus:ring-[#f5a800]/40 focus:border-[#f5a800] transition-shadow"
                  data-testid="export-boekjaar-input" />
              </div>
              <div>
                <label className="text-xs font-medium text-[#526071] block mb-1.5">Betalingsconditie</label>
                <input type="text" value={exportBetalingsconditie} onChange={e => setExportBetalingsconditie(e.target.value)}
                  className="h-9 w-full rounded-lg border border-[#dde3ee] bg-white px-3 text-sm font-mono focus:outline-none focus:ring-2 focus:ring-[#f5a800]/40 focus:border-[#f5a800] transition-shadow"
                  data-testid="export-betalingsconditie-input" />
              </div>
            </div>
            <div className="mt-3 flex flex-wrap items-center gap-3 text-xs text-[#526071]">
              <span>Kopregel: <span className="font-mono font-medium text-[#0b1220]">TW Termijnfactuur {exportPeriode}-{exportBoekjaar}</span></span>
              <span className="text-[#dde3ee]">|</span>
              <span>Factuurdatum: <span className="font-mono font-medium text-[#0b1220]">{lastDayOfMonth(Number(exportBoekjaar), Number(exportPeriode))}</span></span>
              <span className="text-[#dde3ee]">|</span>
              <span>{results.locations.length} locaties</span>
            </div>
          </div>

          {/* Validatiechecks */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className={`tw1-card p-4 flex items-center gap-3 ${holdingBalanceOk ? 'border-l-4 border-[#1e8c45]' : 'border-l-4 border-[#c0392b]'}`} data-testid="validation-holding-balance">
              <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${holdingBalanceOk ? 'bg-[#1e8c45]/10' : 'bg-[#c0392b]/10'}`}>
                {holdingBalanceOk ? <Check className="w-4 h-4 text-[#1e8c45]" /> : <AlertTriangle className="w-4 h-4 text-[#c0392b]" />}
              </div>
              <div>
                <p className="text-sm font-medium text-[#0b1220]">Holding balans</p>
                <p className="text-xs text-[#526071]">Som alle holding regels = <span className="font-mono">{formatEUR(holdingBalance)}</span> {holdingBalanceOk ? '(OK)' : '(NIET 0!)'}</p>
              </div>
            </div>
            <div className={`tw1-card p-4 flex items-center gap-3 ${omzetMatchOk ? 'border-l-4 border-[#1e8c45]' : 'border-l-4 border-[#c0392b]'}`} data-testid="validation-omzet-match">
              <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${omzetMatchOk ? 'bg-[#1e8c45]/10' : 'bg-[#c0392b]/10'}`}>
                {omzetMatchOk ? <Check className="w-4 h-4 text-[#1e8c45]" /> : <AlertTriangle className="w-4 h-4 text-[#c0392b]" />}
              </div>
              <div>
                <p className="text-sm font-medium text-[#0b1220]">Omzet controle</p>
                <p className="text-xs text-[#526071]">Locaties: <span className="font-mono">{formatEUR(locOmzetSum)}</span> = Group: <span className="font-mono">{formatEUR(groupOmzetSum)}</span> {omzetMatchOk ? '(OK)' : '(MISMATCH!)'}</p>
              </div>
            </div>
          </div>

          {/* Locatie exports (per locatie apart) */}
          <div className="tw1-card p-6">
            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 mb-4">
              <div><h3 className="text-lg font-bold text-[#0b1220]">Locatie Exports <span className="text-xs font-normal text-[#526071]">(dagboek 75 — per administratie)</span></h3></div>
              <button onClick={handleExportAlleLocaties} className="flex items-center gap-2 px-4 py-2 rounded-lg bg-[#003366] text-white text-sm font-medium hover:bg-[#00244d] transition-colors" data-testid="export-alle-locaties-btn">
                <Download className="w-3.5 h-3.5" /> Download alle {results.locations.filter(l => l.doorbelasting > 0 || l.fee > 0).length} locaties
              </button>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
              {results.locations.filter(l => l.doorbelasting > 0 || l.fee > 0).map(loc => (
                <button key={loc.locatie} onClick={() => handleExportLocatie(loc)}
                  className="flex items-center gap-3 px-3 py-2.5 rounded-lg border border-[#dde3ee] hover:border-[#003366]/30 hover:bg-[#003366]/[0.02] transition-all text-left group"
                  data-testid={`export-loc-${LOC_CODE[loc.locatie] || 'LOC'}`}>
                  <div className="w-8 h-8 rounded-md bg-[#003366]/8 flex items-center justify-center flex-shrink-0">
                    <span className="text-[10px] font-bold text-[#003366] font-mono">{LOC_CODE[loc.locatie] || '?'}</span>
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-xs font-semibold text-[#0b1220] truncate">{loc.locatie}</p>
                    <p className="text-[10px] text-[#526071] font-mono">{formatEUR(loc.doorbelasting)} / -{formatEUR(loc.fee)}</p>
                  </div>
                  <Download className="w-3.5 h-3.5 text-[#526071] group-hover:text-[#003366] transition-colors flex-shrink-0" />
                </button>
              ))}
            </div>
          </div>

          {/* Group + Holding exports */}
          <div className="tw1-card p-6">
            <h3 className="text-lg font-bold text-[#0b1220] mb-4">Group & Holding Exports</h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <button onClick={handleExportGroup} className="flex items-center gap-3 p-4 rounded-xl border border-[#dde3ee] hover:border-[#f5a800]/30 hover:bg-[#f5a800]/[0.02] transition-all group" data-testid="export-group-btn">
                <div className="w-10 h-10 rounded-lg bg-[#f5a800]/8 flex items-center justify-center flex-shrink-0"><span className="text-xs font-bold text-[#f5a800]">65</span></div>
                <div className="text-left flex-1"><p className="text-sm font-semibold text-[#0b1220]">Export Group <span className="text-[10px] font-normal text-[#526071] ml-1">CSV</span></p><p className="text-[10px] text-[#526071]">Inkoopboeking &middot; {groupRows.length} regels &middot; Importeer via importdefinitie inkoopboek</p></div>
                <Download className="w-4 h-4 text-[#526071] group-hover:text-[#f5a800] transition-colors" />
              </button>
              <button onClick={handleExportHolding} className="flex items-center gap-3 p-4 rounded-xl border border-[#dde3ee] hover:border-[#1e8c45]/30 hover:bg-[#1e8c45]/[0.02] transition-all group" data-testid="export-holding-btn">
                <div className="w-10 h-10 rounded-lg bg-[#1e8c45]/8 flex items-center justify-center flex-shrink-0"><span className="text-xs font-bold text-[#1e8c45]">60</span></div>
                <div className="text-left flex-1"><p className="text-sm font-semibold text-[#0b1220]">Export Holding</p><p className="text-[10px] text-[#526071]">Inkoopboeking &middot; {holdingRows.length} regels</p></div>
                <Download className="w-4 h-4 text-[#526071] group-hover:text-[#1e8c45] transition-colors" />
              </button>
            </div>
          </div>

          {/* Preview tabellen */}
          <div className="tw1-card overflow-hidden" data-testid="export-preview">
            <div className="flex items-center border-b border-gray-100">
              {[{key:'locaties',label:'Locaties (DB75)',count:locatiesRows.length},{key:'group',label:'Group (DB65)',count:groupRows.length},{key:'holding',label:'Holding (DB60)',count:holdingRows.length}].map(tab => (
                <button key={tab.key} onClick={() => setExportPreview(tab.key)} className={`px-5 py-3 text-xs font-medium transition-colors border-b-2 ${exportPreview === tab.key ? 'border-[#003366] text-[#0b1220]' : 'border-transparent text-[#526071] hover:text-[#0b1220]'}`} data-testid={`preview-tab-${tab.key}`}>
                  {tab.label} <span className="ml-1 text-[10px] text-[#526071]">({tab.count})</span>
                </button>
              ))}
            </div>
            <div className="overflow-x-auto max-h-[480px] overflow-y-auto">
              {exportPreview === 'locaties' && (
                <table className="w-full tw1-table text-xs" data-testid="preview-table-locaties">
                  <thead className="sticky top-0 bg-[#f6f7fb] z-10"><tr>{H_LOC.map((h, i) => <th key={i} className="px-3 py-2.5 text-left font-semibold text-[#526071] uppercase tracking-wider whitespace-nowrap">{h}</th>)}</tr></thead>
                  <tbody>{locatiesRows.map((row, i) => <tr key={i} className="border-b border-gray-50 hover:bg-gray-50/50">{row.map((cell, j) => <td key={j} className={`px-3 py-2 whitespace-nowrap ${j===11?'font-mono text-right':''}`}>{j===11?formatBedrag(cell):String(cell)}</td>)}</tr>)}</tbody>
                </table>
              )}
              {exportPreview === 'group' && (
                <table className="w-full tw1-table text-xs" data-testid="preview-table-group">
                  <thead className="sticky top-0 bg-[#f6f7fb] z-10"><tr>{H_GRP.map((h, i) => <th key={i} className="px-3 py-2.5 text-left font-semibold text-[#526071] uppercase tracking-wider whitespace-nowrap">{h}</th>)}</tr></thead>
                  <tbody>{groupRows.map((row, i) => <tr key={i} className="border-b border-gray-50 hover:bg-gray-50/50">{row.map((cell, j) => <td key={j} className={`px-3 py-2 whitespace-nowrap ${j===11?'font-mono text-right':''}`}>{j===11?formatBedrag(cell):String(cell)}</td>)}</tr>)}</tbody>
                </table>
              )}
              {exportPreview === 'holding' && (
                <table className="w-full tw1-table text-xs" data-testid="preview-table-holding">
                  <thead className="sticky top-0 bg-[#f6f7fb] z-10"><tr>{H_HLD.map((h, i) => <th key={i} className="px-3 py-2.5 text-left font-semibold text-[#526071] uppercase tracking-wider whitespace-nowrap">{h}</th>)}</tr></thead>
                  <tbody>{holdingRows.map((row, i) => <tr key={i} className="border-b border-gray-50 hover:bg-gray-50/50">{row.map((cell, j) => <td key={j} className={`px-3 py-2 whitespace-nowrap ${j===9?'font-mono text-right':''}`}>{j===9?formatBedrag(cell):String(cell)}</td>)}</tr>)}</tbody>
                </table>
              )}
            </div>
          </div>

          <div className="flex items-center justify-between">
            <Button variant="outline" onClick={() => setStep(0)} className="text-sm" data-testid="back-to-calc-btn"><ChevronLeft className="w-4 h-4 mr-1" /> Terug naar berekening</Button>
            <Button variant="ghost" size="sm" onClick={clearAll} className="text-xs" data-testid="clear-all-btn"><Trash2 className="w-3 h-3 mr-1" /> Opnieuw beginnen</Button>
          </div>
        </div>
      )}
    </div>
  );
}
